"use client"

import React, { useState } from 'react'
import { ChevronRight, MessageSquare, Phone, MapPin, Send, Loader2, CheckCircle2 } from "lucide-react"
import Script from "next/script"
import Navbar from "@/app/Components/NavbarSection"
import Footer from "@/app/Sections/Footer"

export default function ContactUsPage() {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        subject: "",
        message: "",
        honeypot: "" // For basic spam protection
    })
    const [turnstileError, setTurnstileError] = useState(false)
    const [errors, setErrors] = useState<Record<string, string>>({})

    const validateForm = () => {
        const newErrors: Record<string, string> = {}
        if (!formData.name.trim()) newErrors.name = "Name is required"
        if (!formData.email.trim()) {
            newErrors.email = "Email is required"
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Please enter a valid email address"
        }
        if (!formData.subject.trim()) newErrors.subject = "Subject is required"
        if (!formData.message.trim()) {
            newErrors.message = "Message is required"
        } else if (formData.message.trim().length < 10) {
            newErrors.message = "Message must be at least 10 characters long"
        }
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        
        // Basic spam protection (honeypot)
        if (formData.honeypot) {
            return // Silently reject bots that fill hidden fields
        }

        const formElement = e.target as HTMLFormElement;
        const turnstileResponse = (formElement.querySelector('[name="cf-turnstile-response"]') as HTMLInputElement)?.value;
        
        if (!turnstileResponse) {
            setTurnstileError(true);
            return;
        }
        setTurnstileError(false);

        if (!validateForm()) return

        setIsSubmitting(true)

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ ...formData, turnstileToken: turnstileResponse }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to submit form');
            }

            setIsSuccess(true)
            setFormData({ name: "", email: "", subject: "", message: "", honeypot: "" })
        } catch (error: any) {
            console.error("Form submission error:", error);
            alert(`Error: ${error.message || "There was an error sending your message. Please try again later."}`);
        } finally {
            setIsSubmitting(false)
        }

        // Reset success message after 5 seconds
        setTimeout(() => setIsSuccess(false), 5000)
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: "" }))
        }
    }

    return (
        <div className="bg-black min-h-screen font-poppins">
            <Script src="https://challenges.cloudflare.com/turnstile/v0/api.js" async defer />
            <Navbar />
            
            {/* Hero Section */}
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none">
                    <div
                        className="absolute inset-0"
                        style={{
                            backgroundImage: "url('https://cdn.aurex.lk/assets/hero-mc.webp')",
                            backgroundSize: "cover",
                            backgroundPosition: "center top",
                            filter: "blur(6px) brightness(0.45)",
                            transform: "scale(1.05)",
                        }}
                    />
                    <div className="absolute inset-0 bg-[#3b0d6e]/40" />
                    <div
                        className="absolute inset-0"
                        style={{ background: "radial-gradient(ellipse 90% 60% at 50% 110%, #000000 0%, transparent 90%)" }}
                    />
                    <div
                        className="absolute top-0 left-0 right-0 h-40 pointer-events-none"
                        style={{ background: "linear-gradient(to bottom, #000000, transparent)" }}
                    />
                </div>

                <div className="relative z-10">
                    <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-12 pb-24">
                        <nav className="flex items-center gap-1.5 text-xs font-poppins text-gray-500 mb-8">
                            <a href="/" className="hover:text-white transition-colors">Home</a>
                            <ChevronRight className="w-3 h-3" />
                            <span className="text-white">Contact Us</span>
                        </nav>
                        <h1 className="font-akira text-3xl sm:text-4xl xl:text-5xl text-white leading-tight tracking-wide mb-3">
                            Get In Touch
                        </h1>
                        <p className="text-gray-500 font-poppins text-sm max-w-2xl">
                            Have questions, feedback, or need support? We&apos;re here to help. Reach out to our team directly.
                        </p>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 pointer-events-none" style={{ lineHeight: 0 }}>
                        <svg width="100%" height="50" viewBox="0 0 2598 150" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMaxYMax meet" style={{ display: "block", position: "relative", zIndex: 20 }}>
                            <path d="M2598 150H0L230.359 0H2598V150Z" fill="black" />
                        </svg>
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="relative z-10 w-full bg-black">
                <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12 relative">
                    
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
                        
                        {/* Contact Information */}
                        <div className="flex flex-col gap-8 lg:col-span-1">
                            <div className="bg-[#0a0a14] border border-white/5 rounded-2xl p-6 shadow-xl relative overflow-hidden group">
                                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                <div className="flex items-center gap-4 relative z-10">
                                    <div className="w-12 h-12 bg-indigo-500/10 rounded-xl flex items-center justify-center border border-indigo-500/20 text-indigo-400">
                                        <MessageSquare className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-1">Community Support</p>
                                        <a href="https://discord.com/invite/aurex" target="_blank" rel="noopener noreferrer" className="text-white hover:text-indigo-400 font-medium transition-colors">Join our Discord</a>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-[#0a0a14] border border-white/5 rounded-2xl p-6 shadow-xl relative overflow-hidden group">
                                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                <div className="flex items-center gap-4 relative z-10">
                                    <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center border border-blue-500/20 text-blue-400">
                                        <Phone className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-1">Call Us</p>
                                        <a href="tel:+94770030855" className="text-white hover:text-blue-400 font-medium transition-colors">+94 77 003 0855</a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="lg:col-span-2">
                            <form onSubmit={handleSubmit} className="bg-[#0a0a14] border border-white/5 rounded-3xl p-6 sm:p-10 shadow-xl relative overflow-hidden">
                                <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-600/20 rounded-full blur-[100px] pointer-events-none" />
                                
                                <h2 className="font-akira text-2xl text-white mb-2">Send a Message</h2>
                                <p className="text-gray-400 text-sm mb-8">Fill out the form below and we will get back to you as soon as possible.</p>

                                {isSuccess && (
                                    <div className="mb-8 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-start gap-3">
                                        <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <h4 className="text-emerald-400 font-semibold text-sm">Message Sent Successfully!</h4>
                                            <p className="text-emerald-400/80 text-xs mt-1">Thank you for reaching out. We will get back to you shortly.</p>
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                                    {/* Honeypot field (hidden from real users) */}
                                    <div className="hidden" aria-hidden="true">
                                        <label htmlFor="honeypot">Leave this blank</label>
                                        <input 
                                            type="text" 
                                            name="honeypot" 
                                            id="honeypot" 
                                            value={formData.honeypot} 
                                            onChange={handleChange} 
                                            tabIndex={-1} 
                                            autoComplete="off" 
                                        />
                                    </div>

                                    <div className="flex flex-col gap-2">
                                        <label htmlFor="name" className="text-gray-300 text-sm font-medium">Your Name <span className="text-red-500">*</span></label>
                                        <input 
                                            type="text" 
                                            id="name" 
                                            name="name" 
                                            value={formData.name}
                                            onChange={handleChange}
                                            disabled={isSubmitting}
                                            className={`bg-white/5 border ${errors.name ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500/20' : 'border-white/10 focus:border-purple-500 focus:ring-purple-500/20'} rounded-xl px-4 py-3 text-white placeholder-gray-600 outline-none focus:ring-4 transition-all`}
                                            placeholder="John Doe"
                                        />
                                        {errors.name && <span className="text-red-500 text-xs">{errors.name}</span>}
                                    </div>

                                    <div className="flex flex-col gap-2">
                                        <label htmlFor="email" className="text-gray-300 text-sm font-medium">Email Address <span className="text-red-500">*</span></label>
                                        <input 
                                            type="email" 
                                            id="email" 
                                            name="email" 
                                            value={formData.email}
                                            onChange={handleChange}
                                            disabled={isSubmitting}
                                            className={`bg-white/5 border ${errors.email ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500/20' : 'border-white/10 focus:border-purple-500 focus:ring-purple-500/20'} rounded-xl px-4 py-3 text-white placeholder-gray-600 outline-none focus:ring-4 transition-all`}
                                            placeholder="john@example.com"
                                        />
                                        {errors.email && <span className="text-red-500 text-xs">{errors.email}</span>}
                                    </div>
                                </div>

                                <div className="flex flex-col gap-2 mb-6">
                                    <label htmlFor="subject" className="text-gray-300 text-sm font-medium">Subject <span className="text-red-500">*</span></label>
                                    <input 
                                        type="text" 
                                        id="subject" 
                                        name="subject" 
                                        value={formData.subject}
                                        onChange={handleChange}
                                        disabled={isSubmitting}
                                        className={`bg-white/5 border ${errors.subject ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500/20' : 'border-white/10 focus:border-purple-500 focus:ring-purple-500/20'} rounded-xl px-4 py-3 text-white placeholder-gray-600 outline-none focus:ring-4 transition-all`}
                                        placeholder="How can we help you?"
                                    />
                                    {errors.subject && <span className="text-red-500 text-xs">{errors.subject}</span>}
                                </div>

                                <div className="flex flex-col gap-2 mb-8">
                                    <label htmlFor="message" className="text-gray-300 text-sm font-medium">Message <span className="text-red-500">*</span></label>
                                    <textarea 
                                        id="message" 
                                        name="message" 
                                        rows={5}
                                        value={formData.message}
                                        onChange={handleChange}
                                        disabled={isSubmitting}
                                        className={`bg-white/5 border ${errors.message ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500/20' : 'border-white/10 focus:border-purple-500 focus:ring-purple-500/20'} rounded-xl px-4 py-3 text-white placeholder-gray-600 outline-none focus:ring-4 transition-all resize-none`}
                                        placeholder="Write your message here..."
                                    />
                                    {errors.message && <span className="text-red-500 text-xs">{errors.message}</span>}
                                </div>

                                <div className="mb-6">
                                    <div className="cf-turnstile" data-sitekey="0x4AAAAAADmVmvU-0AjKcG2i" data-theme="dark"></div>
                                    {turnstileError && <p className="text-red-500 text-xs mt-2">Please complete the security check to proceed.</p>}
                                </div>

                                <button 
                                    type="submit" 
                                    disabled={isSubmitting}
                                    className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-semibold text-sm transition-all shadow-[0_0_20px_-5px_rgba(139,92,246,0.5)] flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="w-4 h-4" />
                                            Send Message
                                        </>
                                    )}
                                </button>
                                <p className="text-gray-600 text-xs mt-4">
                                    This site is protected by basic anti-spam measures. By submitting, you agree to our <a href="/privacy" className="text-purple-400 hover:underline">Privacy Policy</a>.
                                </p>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    )
}
